import React from "react";
import { ExpandedState, OnChangeFn } from "@tanstack/react-table";
import { CheckDetailResponse } from "@common/schemas/domains/accounting/check-detail-report.schema";
import { ReportPresentationChrome } from "@common/schemas/domains/reportSettings/report-presentation.schema";
import { NumberFormatValues } from "@common/schemas/domains/reportSettings/number-format-setting.schema";
import { CheckDetailView } from "@common/types/views/checkDetailView";
import { View } from "@common/types/views/views";
import { ViewConfig } from "@lib/views";
import { Card } from "@design-system/Card/Card";
import { CardSection } from "@design-system/Card/CardSection";
import generalLedgerPageStyles from "../generalLedger/GeneralLedgerPageContent.module.scss";
import { AccountingReportFilters } from "../types";
import {
    AccountingReportContext,
    useAccountingReportContextValue,
} from "../AccountingReport.context";
import { AccountingReportHeader } from "../AccountingReportHeader";
import { AccountingReportFooter } from "../AccountingReportFooter";
import { useReportChrome } from "../useReportChrome";
import { CheckDetailTable } from "./CheckDetailTable";
import {
    CheckDetailGroupBy,
    CheckDetailViewRow,
} from "@common/types/reports/check-detail";

interface Props {
    data: CheckDetailResponse;
    groupBy: CheckDetailGroupBy;
    filters: AccountingReportFilters;
    chrome: ReportPresentationChrome;
    numberFormat: NumberFormatValues;
    generationDate: Date;
    expanded: ExpandedState;
    onExpandedChange: OnChangeFn<ExpandedState>;
    view: CheckDetailView;
    onViewChange?: (view: View) => void;
    config: ViewConfig<CheckDetailViewRow, string, never>;
}

export const CheckDetailContent: React.FC<Props> = ({
    data,
    groupBy,
    filters,
    chrome,
    numberFormat,
    generationDate,
    expanded,
    onExpandedChange,
    view,
    onViewChange,
    config,
}) => {
    const resolvedChrome = useReportChrome({ chrome, generationDate });
    const accountingReportContextValue = useAccountingReportContextValue(
        filters,
        numberFormat,
    );

    return (
        <Card stacked sharpEdges fullPage>
            <AccountingReportContext.Provider
                value={accountingReportContextValue}
            >
                <CardSection className={generalLedgerPageStyles.header}>
                    <AccountingReportHeader chrome={resolvedChrome} />
                </CardSection>
                <CheckDetailTable
                    data={data}
                    groupBy={groupBy}
                    expanded={expanded}
                    onExpandedChange={onExpandedChange}
                    view={view}
                    onViewChange={onViewChange}
                    config={config}
                />
                <AccountingReportFooter chrome={resolvedChrome} />
            </AccountingReportContext.Provider>
        </Card>
    );
};
